import React, { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Dumbbell, User, LogOut, Menu, X, Home, Settings } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import BodyweightBadge from "@/components/athlete/BodyweightBadge";
import { AthleteNav } from "@/components/athlete/AthleteNav";

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  // Render the 5-tab athlete nav for users + admins (admin previewing).
  // Skip when we're inside the admin panel itself or assessment portal.
  const showAthleteNav = !!user
    && !location.pathname.startsWith("/admin")
    && !location.pathname.startsWith("/assessments")
    && !location.pathname.startsWith("/affiliate");

  return (
    <>
    <nav className="fixed top-0 left-0 right-0 z-50 glass">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2" data-testid="navbar-logo">
          <Dumbbell className="w-6 h-6 text-primary" />
          <span className="font-heading text-xl font-bold tracking-tight">PALADIN</span>
        </Link>

        {user ? (
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-2">
              {(user.role === "admin" || user.role === "coach") && (
                <Link
                  to="/admin"
                  className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                    location.pathname === "/admin" ? "text-white bg-white/10" : "text-text-secondary hover:text-white"
                  }`}
                >
                  <Settings className="w-4 h-4 inline mr-1" />
                  Coach Panel
                </Link>
              )}
            </div>

            <div className="flex items-center gap-3">
              {(user.role === "user" || user.role === "admin" || user.role === "coach") && <BodyweightBadge />}
              <div className="hidden sm:flex items-center gap-2 text-sm">
                <User className="w-4 h-4 text-text-muted" />
                <span className="text-text-secondary">{user.name}</span>
                <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full capitalize">
                  {user.subscription_tier || user.role}
                </span>
              </div>
              <button
                onClick={handleLogout}
                className="text-text-muted hover:text-white transition-colors p-2"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>

            {/* Mobile Menu */}
            <button
              className="sm:hidden text-text-secondary"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-4">
            <Link to="/pricing" className="text-text-secondary hover:text-white text-sm transition-colors">
              Pricing
            </Link>
            <Link
              to="/login"
              className="bg-primary text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-blue-600 transition-colors"
              data-testid="nav-login"
            >
              Sign In
            </Link>
          </div>
        )}
      </div>

      {/* Mobile dropdown */}
      <AnimatePresence>
        {mobileMenuOpen && user && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="sm:hidden bg-surface border-t border-border px-6 py-4 space-y-2"
          >
            {(user.role === "admin" || user.role === "coach") && (
              <Link to="/admin" className="block py-2 text-text-secondary hover:text-white" onClick={() => setMobileMenuOpen(false)}>
                Coach Panel
              </Link>
            )}
            <div className="pt-2 border-t border-border flex items-center gap-2">
              <User className="w-4 h-4 text-text-muted" />
              <span className="text-text-secondary text-sm">{user.name}</span>
              <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full capitalize">
                {user.subscription_tier || user.role}
              </span>
            </div>
            {user.role === "user" && (
              <div className="pt-2">
                <BodyweightBadge />
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </nav>

    {/* Athlete primary nav — desktop pills under navbar + mobile bottom bar */}
    {showAthleteNav && <AthleteNav />}
    </>
  );
};

export default Navbar;
